import { Microfrontend } from '@cian/mf-react';
import * as React from 'react';
import { useSelector, useStore } from 'react-redux';

import { NewbuildingBrokerModalsContext, TOpeningPlace } from './NewbuildingBrokerModalsContext';
import { useFallbackModalSettings } from './hooks/useFallbackModalSettings';
import { useMicrofrontAPI } from './hooks/useMicrofrontAPI';
import { useMicrofrontEvents } from './hooks/useMicrofrontEvents';
import { useRegionName } from './hooks/useRegionName';
import { mapPlaceToAnalyticsClickType, removeLeadingPlus } from './utils';
import { NewbuildingBrokerFallbackModal } from '../../components/NewbuildingBrokerFallbackModal';
import { getBuilders, getName, getNewbuildingId } from '../../selectors/newbuilding';
import { getIsProfessional, getUser } from '../../selectors/user';
import { IApplicationState } from '../../types/redux';
import { IUser } from '../../types/currentUser';

/**
 * Компонент, обеспечивающий загрузку микрофронта модалок бронирования на клиенте.
 *
 * 1. Пользователь кликает на точку входа, и вызывается `startMicrofrontOpening`.
 * 2. Регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализируется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка.
 */
export const NewbuildingBrokerModalsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const newbuildingName = useSelector(getName);
  const newbuildingId = useSelector(getNewbuildingId);
  const builders = useSelector(getBuilders);
  const regionName = useRegionName();
  const fallbackModalSettings = useFallbackModalSettings();
  const store = useStore<IApplicationState>();
  const [loadingPlace, setLoadingPlace] = React.useState<TOpeningPlace | null>(null);
  const [isFallbackModalVisible, setFallbackModalVisible] = React.useState(false);
  const openingRef = React.useRef(false);
  const lastLoadingPlaceRef = React.useRef<TOpeningPlace | null>(null);

  const finishMicrofrontOpening = React.useCallback((isError: boolean) => {
    if (isError) {
      openingRef.current = false;
      setLoadingPlace(null);
    }

    // Снятие loading при успешной загрузке произойдет по событию из микрофронта.
    setFallbackModalVisible(isError);
  }, []);

  const { microfrontManifest, microfrontRef, openMicrofront } = useMicrofrontAPI(finishMicrofrontOpening);

  const handleLoadingPlaceChange = React.useCallback((place: TOpeningPlace | null) => {
    openingRef.current = place !== null;
    setLoadingPlace(place);
  }, []);

  useMicrofrontEvents(microfrontManifest, lastLoadingPlaceRef, handleLoadingPlaceChange);

  const startMicrofrontOpening = React.useCallback(
    (place: TOpeningPlace) => {
      if (openingRef.current) {
        return;
      }

      openingRef.current = true;
      setLoadingPlace(place);

      if (place !== 'fallbackModal') {
        lastLoadingPlaceRef.current = place;
      }

      // Не используем user в зависимостях во избежание проблемы с замыканием на старое значение.
      const user = getUser(store.getState()) as IUser;
      const isProfi = getIsProfessional(store.getState());

      void openMicrofront({
        newbuildingName,
        newbuildingId,
        regionName,
        builderNames: builders.map(builder => builder.name),
        user: {
          firstName: user.firstName,
          phones: user.phone ? [{ phone: removeLeadingPlus(user.phone) }] : [],
          isProfi,
        },
        analyticsClickType: mapPlaceToAnalyticsClickType(lastLoadingPlaceRef.current as TOpeningPlace),
      });
    },
    [builders, newbuildingId, newbuildingName, openMicrofront, regionName, store],
  );

  const handleRetry = React.useCallback(() => {
    startMicrofrontOpening('fallbackModal');
  }, [startMicrofrontOpening]);

  const handleClose = React.useCallback(() => {
    setFallbackModalVisible(false);
  }, []);

  const value = React.useMemo(() => ({ loadingPlace, startMicrofrontOpening }), [loadingPlace, startMicrofrontOpening]);

  return (
    <NewbuildingBrokerModalsContext.Provider value={value}>
      {children}
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          {...fallbackModalSettings}
          loading={loadingPlace === 'fallbackModal'}
          onClose={handleClose}
          onRetry={handleRetry}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </NewbuildingBrokerModalsContext.Provider>
  );
};
